import { pipe, filter, map, unique } from 'remeda';
import type { EnrichedRecord } from './dataTransformations';

export type { EnrichedRecord };

type StatusFilter = (record: EnrichedRecord) => boolean;
type AppFilter = (record: EnrichedRecord) => boolean;
type NameSearchFilter = (record: EnrichedRecord) => boolean;
type DateRangeFilter = (record: EnrichedRecord) => boolean;

export const createStatusFilter =
  (selectedStatuses: string[]): StatusFilter =>
  (record: EnrichedRecord) => {
    return selectedStatuses.includes(record.status);
  };

export const createAppFilter =
  (appFilter: string | null): AppFilter =>
  (record: EnrichedRecord) => {
    if (!appFilter) return true;
    return record.type === appFilter;
  };

export const createNameSearchFilter =
  (nameSearch: string): NameSearchFilter =>
  (record: EnrichedRecord) => {
    if (!nameSearch) return true;

    const searchLower = nameSearch.toLowerCase();
    return (
      record.patientCode?.toLowerCase().includes(searchLower) ||
      record.deviceName?.toLowerCase().includes(searchLower) ||
      record.videoName?.toLowerCase().includes(searchLower) ||
      (record.id ? String(record.id) : '').includes(searchLower) ||
      false
    );
  };

export const createDateRangeFilter =
  (dateRange: [Date | null, Date | null]): DateRangeFilter =>
  (record: EnrichedRecord) => {
    const [startDate, endDate] = dateRange;

    if (!startDate && !endDate) return true;

    const recordDate = new Date(record.created_at || record.updated_at || '');

    if (startDate && recordDate < startDate) return false;
    if (endDate && recordDate > endDate) return false;

    return true;
  };

export const combineFilters =
  (
    ...filters: Array<(record: EnrichedRecord) => boolean>
  ): ((record: EnrichedRecord) => boolean) =>
  (record: EnrichedRecord) => {
    return filters.every(filterFn => filterFn(record));
  };

export const applyFilters = (
  records: EnrichedRecord[],
  statusFilter: StatusFilter,
  appFilter: AppFilter,
  nameSearchFilter: NameSearchFilter,
  dateRangeFilter: DateRangeFilter
): EnrichedRecord[] => {
  const combinedFilter = combineFilters(
    statusFilter,
    appFilter,
    nameSearchFilter,
    dateRangeFilter
  );

  return pipe(records, filter(combinedFilter));
};

export const extractAvailableStatuses = (
  records: EnrichedRecord[]
): string[] => {
  return pipe(
    records,
    filter(record => Boolean(record.status)),
    map(record => record.status),
    unique()
  );
};
